import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Settings, Save, Stethoscope, Heart } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { SessionReminderSettings } from "./SessionReminderSettings";
import { BankDetailsSettings } from "./BankDetailsSettings";

interface ClinicianSettingsProps {
  profile: any;
  subscription: any;
  onUpdateProfile: (updates: any) => Promise<any>;
}

export function ClinicianSettings({ profile, subscription, onUpdateProfile }: ClinicianSettingsProps) {
  const [formData, setFormData] = useState({
    practice_name: profile?.practice_name || '',
    professional_title: profile?.professional_title || '',
    practitioner_type: profile?.practitioner_type || 'therapist',
    bio: profile?.bio || '',
    practice_email: profile?.practice_email || '',
    practice_phone: profile?.practice_phone || '',
    practice_address: profile?.practice_address || '',
    standard_session_fee: profile?.standard_session_fee || '',
    cancellation_notice_hours: profile?.cancellation_notice_hours || 24,
    cancellation_fee_percentage: profile?.cancellation_fee_percentage || 50
  });
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdateProfile(formData);
      toast({ title: "Settings saved" });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-card/50 border-border/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-foreground">
            <Settings className="h-5 w-5 text-primary" />
            Practice Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Practitioner Type Selection */}
          <div className="space-y-3">
            <Label className="text-foreground">Practitioner Type</Label>
            <RadioGroup
              value={formData.practitioner_type}
              onValueChange={(value) => setFormData(p => ({ ...p, practitioner_type: value }))}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="therapist" id="therapist" className="border-primary text-primary" />
                <Label htmlFor="therapist" className="flex items-center gap-2 cursor-pointer text-foreground">
                  <Stethoscope className="h-4 w-4 text-primary" />
                  Therapist
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="coach" id="coach" className="border-primary text-primary" />
                <Label htmlFor="coach" className="flex items-center gap-2 cursor-pointer text-foreground">
                  <Heart className="h-4 w-4 text-secondary" />
                  Coach
                </Label>
              </div>
            </RadioGroup>
            <p className="text-xs text-muted-foreground">
              This determines how you appear to clients and the terminology used throughout the portal
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-foreground">Practice Name</Label>
              <Input 
                value={formData.practice_name} 
                onChange={e => setFormData(p => ({ ...p, practice_name: e.target.value }))} 
                className="bg-input border-border text-foreground"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground">Professional Title</Label>
              <Input 
                value={formData.professional_title} 
                onChange={e => setFormData(p => ({ ...p, professional_title: e.target.value }))} 
                className="bg-input border-border text-foreground"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-foreground">Bio</Label>
            <Textarea 
              value={formData.bio} 
              onChange={e => setFormData(p => ({ ...p, bio: e.target.value }))} 
              rows={3} 
              className="bg-input border-border text-foreground"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-foreground">Practice Address</Label>
            <Textarea 
              value={formData.practice_address} 
              onChange={e => setFormData(p => ({ ...p, practice_address: e.target.value }))} 
              rows={2} 
              className="bg-input border-border text-foreground"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-foreground">Email</Label>
              <Input 
                value={formData.practice_email} 
                onChange={e => setFormData(p => ({ ...p, practice_email: e.target.value }))} 
                className="bg-input border-border text-foreground"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground">Phone</Label>
              <Input 
                value={formData.practice_phone} 
                onChange={e => setFormData(p => ({ ...p, practice_phone: e.target.value }))} 
                className="bg-input border-border text-foreground"
              />
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-foreground">Session Fee (£)</Label>
              <Input 
                type="number" 
                value={formData.standard_session_fee} 
                onChange={e => setFormData(p => ({ ...p, standard_session_fee: e.target.value }))} 
                className="bg-input border-border text-foreground"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground">Cancellation Notice (hrs)</Label>
              <Input 
                type="number" 
                value={formData.cancellation_notice_hours} 
                onChange={e => setFormData(p => ({ ...p, cancellation_notice_hours: Number(e.target.value) }))} 
                className="bg-input border-border text-foreground"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground">Cancellation Fee (%)</Label>
              <Input 
                type="number" 
                value={formData.cancellation_fee_percentage} 
                onChange={e => setFormData(p => ({ ...p, cancellation_fee_percentage: Number(e.target.value) }))} 
                className="bg-input border-border text-foreground"
              />
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving} className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90">
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </CardContent>
      </Card>

      <SessionReminderSettings profile={profile} />
      
      <BankDetailsSettings profile={profile} />
    </div>
  );
}
